import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import {
    AppliedBillableConceptCreateRequest,
    AppliedBillableConceptExtraFieldsCreateRequest,
    AppliedBillableConceptExtraFieldsPatchInBatchRequest,
    AppliedBillableConceptPatchInBatchRequest,
    AppliedBillableConceptResponse,
    HierarchicalBillingCaseTreeResponse,
    SentCustomNotificationResponse,
    SumPerAccountingUnitResponse
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { ReportGridRequest } from '@/api/services/common.dto'

export enum BillingAppliedBillableConceptCategory {
    HAS_NO_CUSTOMER = 'hasNoCustomer',
    HAS_CUSTOMER = 'hasCustomer',
    HAS_ERRORS = 'hasErrors'
}

class BillingAppliedBillableConceptService {
    async delete(
        laboratoryId: number,
        request: EntityIdentifiersRequest
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.delete<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/batch`,
                { data: request })
        return response.data
    }

    async get(
        laboratoryId: number,
        appliedBillableConceptId: number
    ): Promise<AppliedBillableConceptResponse> {
        const response =
            await axios.get<AppliedBillableConceptResponse>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/${appliedBillableConceptId}`)
        return response.data
    }

    async getSentCustomNotifications(
        laboratoryId: number,
        appliedBillableConceptId: number
    ): Promise<SentCustomNotificationResponse[]> {
        const response =
            await axios.get<SentCustomNotificationResponse[]>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/${appliedBillableConceptId}/customNotificationsSent`)
        return response.data
    }

    async getSumPerAccountingUnit(
        laboratoryId: number,
        caseId: number
    ): Promise<SumPerAccountingUnitResponse[]> {
        const response =
            await axios.get<SumPerAccountingUnitResponse[]>(
                `/billing/laboratories/${laboratoryId}/cases/${caseId}/appliedBillableConcepts/sumPerAccountingUnit`)
        return response.data
    }

    async getHierarchicalBillingCaseTree(
        laboratoryId: number,
        caseId: number,
        accountingUnitId: number
    ): Promise<HierarchicalBillingCaseTreeResponse> {
        const response =
            await axios.get<HierarchicalBillingCaseTreeResponse>(
                `/billing/laboratories/${laboratoryId}/cases/${caseId}/accountingUnit/${accountingUnitId}/appliedBillableConcepts/hierarchicalBillingCaseTree`)
        return response.data
    }

    async patch(
        laboratoryId: number,
        request: AppliedBillableConceptPatchInBatchRequest[]
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.patch<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/batch`,
                request)
        return response.data
    }

    async patchWithExtraFields(
        laboratoryId: number,
        request: AppliedBillableConceptExtraFieldsPatchInBatchRequest[]
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.patch<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/batch/extra`,
                request)
        return response.data
    }

    async create(
        laboratoryId: number,
        request: AppliedBillableConceptCreateRequest
    ): Promise<AppliedBillableConceptResponse> {
        const response =
            await axios.post<AppliedBillableConceptResponse>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts`,
                request)
        return response.data
    }

    async createWithExtraFields(
        laboratoryId: number,
        request: AppliedBillableConceptExtraFieldsCreateRequest
    ): Promise<AppliedBillableConceptResponse> {
        const response =
            await axios.post<AppliedBillableConceptResponse>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/extra`,
                request)
        return response.data
    }

    async paginator(
        filterModel: Nullable<CommonFilterModel>,
        params: Nullable<CommonParams>,
        laboratoryId: number,
        category?: BillingAppliedBillableConceptCategory
    ): Promise<IResponseAgGrid<AppliedBillableConceptResponse>> {
        const mode = category ? `/${category}` : ''
        const response =
            await axios.put<IResponseAgGrid<AppliedBillableConceptResponse>>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts${mode}`,
                { ...filterModel },
                { params })
        return response.data
    }

    async export(
        laboratoryId: number,
        gridColumns?: ReportGridRequest,
        params?: ExportCommonParams,
        category?: BillingAppliedBillableConceptCategory
    ): Promise<AxiosResponse<Blob>> {
        const mode = category ? `/${category}` : ''
        return axios.put<Blob>(
            `/billing/laboratories/${laboratoryId}/appliedBillableConcepts${mode}/export`,
            { ...gridColumns },
            { params, responseType: 'arraybuffer' })
    }

    async requestAuthorization(
        laboratoryId: number,
        appliedBillableConceptId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/appliedBillableConcepts/${appliedBillableConceptId}/requestAuthorization`)
        return response.data
    }
}
export const billingAppliedBillableConceptService = new BillingAppliedBillableConceptService()
